<template>
  <div>
    <AppContainer v-if="!showSignOff" :is-loading>
      <div>
        <h1 class="mb-2 text-center text-xl font-bold">
          {{ t(useTranslationPath(callerPath, 'checkOut')) }}
        </h1>
        <p class="mb-8 text-center">
          {{ t(useTranslationPath(callerPath, 'completeBeforeLeaving')) }}
        </p>
        <EForm>
          <EFormField
            id="closure_reason"
            :label="t(useTranslationPath(callerPath, 'workOrderResolution'))"
            fieldset
            :errors="getFieldErrors('closure_reason')"
          >
            <ERadioGroup v-model="v$.closure_reason.$model">
              <ERadio
                id="service"
                name="service"
                :label="t(useTranslationPath(callerPath, 'serviceCompleted.label'))"
                value="NO_FURTHER_ACTION"
                :hint="t(useTranslationPath(callerPath, 'serviceCompleted.hint'))"
              />
              <ERadio
                id="quote"
                name="quote"
                :label="t(useTranslationPath(callerPath, 'quoteRequired.label'))"
                value="FOLLOW_ON_QUOTE_REQUIRED"
                :hint="t(useTranslationPath(callerPath, 'quoteRequired.hint'))"
              />
              <ERadio
                id="return"
                name="return"
                :label="t(useTranslationPath(callerPath, 'returnVisitRequired.label'))"
                value="ANOTHER_VISIT_REQUIRED"
                :hint="t(useTranslationPath(callerPath, 'returnVisitRequired.hint'))"
              />
            </ERadioGroup>
          </EFormField>
          <EFormField
            id="visit_notes"
            :label="t(useTranslationPath(callerPath, 'visitNotes'))"
            :errors="getFieldErrors('visit_notes')"
          >
            <ETextArea
              id="visit_notes"
              v-model="v$.visit_notes.$model"
              name="visit_notes"
              :placeholder="t(useTranslationPath(callerPath, 'enterNotes'))"
            />
          </EFormField>
          <EInputFile
            id="files"
            v-model="v$.files.$model"
            multiple
            :drop-zone="false"
            :label-select="t(useTranslationPath(callerPath, 'uploadCheckout'))"
            :accepts
            :max-size="maxFileSize"
            :external-errors="fileValidationErrors"
            @errors="onFileValidation"
          />
        </EForm>
      </div>

      <template #bottom>
        <EButton
          :label="t(useTranslationPath(callerPath, requiresSignOff ? 'continue' : 'checkOut'))"
          :disabled="isLoading || v$.$invalid"
          block
          @click="requiresSignOff ? handleSignOff() : handleContinue()"
        />
      </template>
    </AppContainer>
    <CodeVerification
      v-else
      :description="t(useTranslationPath(callerPath, 'signOff'))"
      :site-id="authStore.maintainableEntity.id"
      :is-loading
      :action-label="t(useTranslationPath(callerPath, 'checkOut'))"
      @valid="handleContinue"
    />
  </div>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import CodeVerification from '@/components/Verification/CodeVerification.vue'
import { useGetConfigVar } from '@/composables/useGetConfigVar.js'
import { useGoBack } from '@/composables/useGoBack.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { isAccessCodeEnabled, isLocationEnabled } from '@/composables/useVerificationMethod.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useAuthStore } from '@/stores/authenticated/AuthStore'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore'
import { useStartWorkCheckStore } from '@/stores/authenticated/StartWorkCheckStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore.js'
import {
  EButton,
  EForm,
  EFormField,
  EInputFile,
  ERadio,
  ERadioGroup,
  ETextArea,
} from '@expansivefm/eds'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'

const { t } = useI18n()
const { setCustomGoBack } = useGoBack()

const route = useRoute()
const router = useRouter()

const appStore = useAppStore()
const authStore = useAuthStore()
const engineerStore = useEngineerStore()
const workOrderStore = useWorkOrderStore()
const startWorkCheckStore = useStartWorkCheckStore()

const callerPath = 'views.authenticated.workOrders.checkout.index'

const accepts = ref({
  'image/*': 'Images (.jpg, .jpeg, .png, .gif etc...)',
  'video/*': 'Videos (.mp4, .mov, .avi etc...)',
  'text/csv': 'CSV',
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLXS',
})

const fileValidationErrors = ref({})

const isLoading = ref(false)
const showSignOff = ref(false)

const form = reactive({
  closure_reason: null,
  visit_notes: null,
  files: [],
})

const requiresSignOff = computed(() => {
  const closureReasons = useGetConfigVar('ENGINEER_APP_WORK_ORDER_CLOSURE_SIGN_OFF')

  if (!closureReasons) {
    return false
  }

  return closureReasons.find((closureReason) => closureReason === form.closure_reason) !== undefined
})

const maxFileSize = computed(
  () => (useGetConfigVar('MAX_FILE_UPLOAD_SIZE') || 104857600) / (1024 * 1024),
)

const exactFileNameMap = computed(() => {
  const map = {}
  for (const f of form.files) {
    map[toLowerTrim(f.name)] = f.name
  }
  return map
})

const collapsedFileNameMap = computed(() => {
  const map = {}
  for (const f of form.files) {
    map[normalizeCollapseSpaces(f.name)] = f.name
  }
  return map
})

const nospaceFileNameMap = computed(() => {
  const map = {}
  for (const f of form.files) {
    map[normalizeNoSpaces(f.name)] = f.name
  }
  return map
})

const filesShouldNotHaveErrors = () => {
  return Object.keys($externalResults.value.files || {}).length === 0
}

const validationRules = {
  closure_reason: { required },
  visit_notes: { required },
  files: { filesShouldNotHaveErrors },
}

const $externalResults = ref({})

const v$ = useVuelidate(validationRules, form, { $externalResults })

onMounted(() => {
  setCustomGoBack(() => {
    router.push({
      name: 'work-order.detail',
      params: {
        workOrderId: route.params.workOrderId,
      },
    })
  })

  appStore.$patch({ showBackButton: true })
})

const onFileValidation = (fileErrors) => {
  $externalResults.value = { ...$externalResults.value, files: fileErrors }
}

const getFieldErrors = (field) => {
  return v$.value[field].$errors.map((error) => error.$message)
}

const toLowerTrim = (s) => (s || '').trim().toLowerCase()
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const normalizeCollapseSpaces = (s) => toLowerTrim(s).replace(/\s+/g, ' ')
const normalizeNoSpaces = (s) => toLowerTrim(s).replace(/\s+/g, '')

const resolveOriginalFileName = (rawName) => {
  const raw = rawName || ''
  const exact = exactFileNameMap.value[toLowerTrim(raw)]

  if (exact) return exact

  const collapsed = collapsedFileNameMap.value[normalizeCollapseSpaces(raw)]

  if (collapsed) return collapsed

  const nospace = nospaceFileNameMap.value[normalizeNoSpaces(raw)]

  return nospace ? nospace : raw.trim()
}

const handleSignOff = () => {
  showSignOff.value = true
}

const handleContinue = async () => {
  showSignOff.value = false
  v$.value.$touch()
  v$.value.$clearExternalResults()

  if (!(await v$.value.$validate())) {
    return
  }

  isLoading.value = true

  if (form.closure_reason === null) {
    $externalResults.value = {
      ...$externalResults.value,
      closure_reason: [t(useTranslationPath(callerPath, 'noResolutionSelected'))],
    }

    isLoading.value = false

    return
  }

  let sessionData = {
    status: 'CLOSED',
    closure_reason: form.closure_reason,
    visit_notes: form.visit_notes,
    location_verification_method_check_out: null,
    workOrderStartWorkCheck: workOrderStore.workOrder.workflow_start_work_checks_required
      ? startWorkCheckStore.check?.id
      : null,
    work_order_closure_sign_off: requiresSignOff.value,
  }

  if (
    (isLocationEnabled('ENGINEER_APP_CHECK_OUT_METHOD') ||
      isAccessCodeEnabled('ENGINEER_APP_CHECK_OUT_METHOD')) &&
    engineerStore.verificationData.checkOut.method !== null
  ) {
    sessionData = {
      ...sessionData,
      location_verification_method_check_out: engineerStore.verificationData.checkOut.method,
    }

    if (engineerStore.verificationData.checkOut.method === 'GEOLOCATION') {
      sessionData = {
        ...sessionData,
        geolocation_check_out: engineerStore.verificationData.checkOut.data,
      }
    }
  }

  await engineerStore
    .updateSession(engineerStore.session.id, sessionData, form.files)
    .then(async () => {
      await router.push({
        name: 'work-order.checkout.success',
        params: {
          workOrderId: route.params.workOrderId,
        },
      })
    })
    .catch(({ code, response: { data } }) => {
      isLoading.value = false

      if (data.errors) {
        data.errors.forEach((errorMessage) => {
          if (typeof errorMessage === 'object' && errorMessage !== null) {
            if (errorMessage.detail.startsWith('The files.')) {
              const matchResult = errorMessage.detail.match(
                /files\.\d+\.files\.\s*([\s\S]*?)(?=\s+must be|$)/i,
              )
              if (matchResult && matchResult[1]) {
                const rawName = matchResult[1]
                const keyName = resolveOriginalFileName(rawName)

                let cleanedMessage = errorMessage.detail.replace(/The files\.\d+\.files\.\s*/i, '')
                // Remove only the first occurrence of rawName (with optional surrounding whitespace)
                const rawNamePattern = new RegExp(`\\s*${escapeRegExp(rawName)}\\s*`, 'i')
                cleanedMessage = cleanedMessage.replace(rawNamePattern, '').trim()

                fileValidationErrors.value[keyName] = cleanedMessage
              }
            }
          }
        })
      }

      toast(
        t(
          useTranslationPath(
            callerPath,
            code === 'ECONNABORTED' ? 'errors.timedOut' : 'errors.closingSession',
          ),
        ),
        {
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_CENTER,
          type: 'error',
        },
      )
    })
}
</script>
